import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  getPublishedChronicles,
  getChronicleCategories,
} from "../../services/chroniclesApi";
import {
  getChronicleImageUrl,
  formatChronicleDate,
} from "../../utils/chronicleContentRenderer";
import { CHRONICLES_SEO } from "../../constants/siteSeo";
import { usePageSeo } from "../../hooks/usePageSeo";
import { dashboardAsset } from "../../utils/dashboardAssets";
import "./chronicles.css";

/** Cinematic Oshar / Treydora city vista from dashboard pack (public/assets/dashboard). */
const CHRONICLES_HERO_BG = dashboardAsset("01_hero_oshar_city_spires.png");

const LISTING_DESCRIPTION =
  "Explore official records, lore, updates, and transmissions from Treydora, Oshar, Treydorex, and the world of Rise of Koraxos.";

const PRESET_FILTERS = [
  { id: "lore", label: "Lore", category: "Lore" },
  { id: "game-development", label: "Game Development", category: "Game Development" },
  { id: "oshar", label: "Oshar", category: "Oshar" },
  { id: "treydorex", label: "Treydorex", category: "Treydorex" },
  { id: "nft-comic", label: "NFT Comic", category: "NFT Comic" },
  { id: "founder-updates", label: "Founder Updates", category: "Founder Updates" },
  { id: "announcements", label: "Announcements", category: "Announcements" },
];

function latestTransmissionDate(posts) {
  let best = 0;
  posts.forEach((p) => {
    const t = p?.publishedAt ? new Date(p.publishedAt).getTime() : 0;
    if (t > best) best = t;
  });
  if (!best) return "—";
  return formatChronicleDate(new Date(best).toISOString());
}

export default function ChroniclesPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState("");

  const load = useCallback(async (category) => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        limit: 48,
        sort: "-publishedAt",
        ...(category ? { category } : {}),
      };
      const [catList, listRes] = await Promise.all([
        getChronicleCategories(),
        getPublishedChronicles(params),
      ]);
      setCategories(Array.isArray(catList) ? catList : []);
      setPosts(listRes.posts || []);
      setTotal(listRes.total ?? 0);
    } catch (e) {
      setError(e);
      setPosts([]);
      setTotal(0);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(activeCategory || "");
  }, [load, activeCategory]);

  usePageSeo({
    ...CHRONICLES_SEO,
    description: LISTING_DESCRIPTION,
    ogDescription: LISTING_DESCRIPTION,
  });

  const filterChips = useMemo(() => {
    const apiNames = new Set(
      (categories || []).map((c) => String(c.name || "").trim().toLowerCase()).filter(Boolean)
    );
    const fromApi = (categories || []).map((c) => ({
      id: `api-${c._id || c.slug || c.name}`,
      label: String(c.name || "Category").trim(),
      category: String(c.name || "").trim(),
    }));
    const presets = PRESET_FILTERS.filter(
      (p) => p.category && !apiNames.has(p.category.trim().toLowerCase())
    );
    return [{ id: "all", label: "All", category: "" }, ...fromApi, ...presets];
  }, [categories]);

  const { featured, gridPosts } = useMemo(() => {
    const list = posts || [];
    const feat = list.find((p) => p.featured) || list[0] || null;
    if (!feat) return { featured: null, gridPosts: [] };
    const slug = feat.slug;
    const grid = list.filter((p) => p.slug !== slug);
    return { featured: feat, gridPosts: grid };
  }, [posts]);

  const scrollToArchive = () => {
    const el = document.getElementById("chr-archive");
    el?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="chr">
      <section
        id="chr-hero"
        className="chr-hero"
        aria-labelledby="chr-hero-title"
        style={{ "--chr-hero-img": `url(${CHRONICLES_HERO_BG})` }}
      >
        <div className="chr-hero__bg-layer" aria-hidden />
        <div className="chr-hero__glow-title" aria-hidden />
        <div className="chr-hero__scanlines" aria-hidden />
        <div className="chr-hero__grid" aria-hidden />
        <div className="chr-hero__brackets" aria-hidden />
        <div className="chr-hero__inner">
          <div className="chr-hero__label chr-hud">TREYDORA ARCHIVE</div>
          <h1 id="chr-hero-title" className="chr-hero__title chr-serif">
            The Chronicles
          </h1>
          <p className="chr-hero__subtitle">
            Records from Oshar, Treydora, and the worlds still being forged.
          </p>
          <button type="button" className="chr-hero__cta" onClick={scrollToArchive}>
            Explore the Archive
          </button>

          <div className="chr-stats" aria-label="Archive statistics">
            <div className="chr-stat">
              <div className="chr-stat__label">Published Records</div>
              <div className="chr-stat__value">{loading ? "—" : total}</div>
            </div>
            <div className="chr-stat">
              <div className="chr-stat__label">Active Categories</div>
              <div className="chr-stat__value">{categories.length}</div>
            </div>
            <div className="chr-stat">
              <div className="chr-stat__label">Latest Transmission</div>
              <div className="chr-stat__value chr-serif" style={{ fontSize: "1.05rem" }}>
                {loading ? "—" : latestTransmissionDate(posts)}
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="chr-rule" aria-hidden />

      <div className="chr-inner">
        <div id="chr-archive" className="chr-filters">
          <div className="chr-filters__row" role="tablist" aria-label="Filter records">
            {filterChips.map((chip) => (
              <button
                key={chip.id}
                type="button"
                role="tab"
                aria-selected={(activeCategory || "") === (chip.category || "")}
                className={`chr-filter-chip${
                  (activeCategory || "") === (chip.category || "") ? " is-active" : ""
                }`}
                onClick={() => setActiveCategory(chip.category || "")}
              >
                {chip.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="chr-state" role="status">
            <p className="chr-state__title chr-serif">Opening the archive…</p>
            <p className="chr-state__text">Retrieving transmissions from the vault.</p>
          </div>
        ) : null}

        {error && !loading ? (
          <div className="chr-state" role="alert">
            <p className="chr-state__title chr-serif">The archive could not be reached.</p>
            <p className="chr-state__text">
              The vault connection failed. Confirm the API is running and{" "}
              <code style={{ color: "var(--chr-gold)" }}>REACT_APP_API_BASE_URL</code> points to your TreydoraED
              backend. If the browser blocks the request, check CORS on the server.
            </p>
          </div>
        ) : null}

        {!loading && !error && posts.length === 0 ? (
          <div className="chr-state chr-state--vault">
            <p className="chr-state__title chr-serif">No Treydora Chronicles Published Yet</p>
            <p className="chr-state__text">
              Create a post in TreydoraED and select Treydora Chronicles as a publish destination.
            </p>
            <button type="button" className="chr-btn-primary" style={{ marginTop: 22 }} onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
              Return to Archive
            </button>
          </div>
        ) : null}

        {!loading && !error && featured ? (
          <section className="chr-featured" aria-labelledby="chr-featured-title">
            <div className="chr-featured__card">
              <div className="chr-featured__media">
                <img src={getChronicleImageUrl(featured)} alt="" loading="eager" />
              </div>
              <div className="chr-featured__body">
                {featured.category ? <span className="chr-badge">{featured.category}</span> : null}
                <h2 id="chr-featured-title" className="chr-featured__title">
                  {featured.title}
                </h2>
                {featured.excerpt ? <p className="chr-featured__excerpt">{featured.excerpt}</p> : null}
                <div className="chr-meta-row">
                  <span>{formatChronicleDate(featured.publishedAt)}</span>
                  {featured.authorName ? <span>{featured.authorName}</span> : null}
                  {featured.readingTimeMinutes != null ? (
                    <span>{featured.readingTimeMinutes} min read</span>
                  ) : null}
                </div>
                <Link className="chr-btn-primary" to={`/chronicles/${encodeURIComponent(featured.slug)}`}>
                  Read Chronicle
                </Link>
              </div>
            </div>
          </section>
        ) : null}

        {!loading && !error && gridPosts.length > 0 ? (
          <section className="chr-grid" aria-label="Chronicle records">
            {gridPosts.map((post) => (
              <article key={post._id || post.slug} className="chr-card">
                <div className="chr-card__img-wrap">
                  <img src={getChronicleImageUrl(post)} alt="" loading="lazy" />
                </div>
                <div className="chr-card__body">
                  {post.category ? <span className="chr-badge">{post.category}</span> : null}
                  <h3 className="chr-card__title">{post.title}</h3>
                  {post.excerpt ? <p className="chr-card__excerpt">{post.excerpt}</p> : null}
                  {Array.isArray(post.tags) && post.tags.length ? (
                    <div className="chr-tags">
                      {post.tags.slice(0, 5).map((t) => (
                        <span key={t} className="chr-tag">
                          {t}
                        </span>
                      ))}
                    </div>
                  ) : null}
                  <div className="chr-card__foot">
                    <span>{formatChronicleDate(post.publishedAt)}</span>
                    <span>{post.authorName || "Archive"}</span>
                  </div>
                  <Link className="chr-btn-primary" to={`/chronicles/${encodeURIComponent(post.slug)}`} style={{ marginTop: 12 }}>
                    Read Chronicle
                  </Link>
                </div>
              </article>
            ))}
          </section>
        ) : null}
      </div>
    </div>
  );
}
